import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Lock, Mail, Eye, EyeOff, LogIn, Globe, MailIcon, ShieldCheck, Instagram } from 'lucide-react';
import Auth3DShowcase from './Auth3DShowcase';
import './login.css';

/**
 * Cinematic 3D Login Page
 * Left pane  : shared <Auth3DShowcase /> (5 rotating AV photos, brand badge).
 * Right pane : Glassmorphism sign-in card with gradient CTA.
 */

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Honour the `from` redirect passed in via location.state OR ?next= query
  // string so a user who clicked "Simulation" on the landing page (or
  // "Sign in" inside the Test Pattern Generator) lands back on that page
  // instead of being bounced to the home page.
  const fromState = location.state?.from;
  const fromQuery = new URLSearchParams(location.search).get('next');
  const redirectTo = fromState || fromQuery || '/';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full max-w-full bg-black grid grid-cols-1 lg:grid-cols-[1.1fr_minmax(420px,0.9fr)] overflow-x-hidden"
      data-testid="login-page"
    >
      {/* ── LEFT : 3D cinematic showcase ── */}
      <Auth3DShowcase />

      {/* ── RIGHT : Sign-in glass card ── */}
      <div
        className="relative flex items-center justify-center p-6 sm:p-10 auth-grain"
        style={{ background: 'radial-gradient(ellipse at center, #0a0d1c 0%, #05060f 60%, #000 100%)' }}
      >
        {/* Soft accent glow behind the card */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-fuchsia-600/10 blur-3xl pointer-events-none" />
        <div className="absolute left-1/3 top-1/3 w-[360px] h-[360px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

        <div className="relative w-full max-w-md auth-fade-up">
          {/* Brand heading (right pane) */}
          <div className="flex flex-col items-center mb-8">
            <Link
              to="/"
              data-testid="login-brand-link"
              className="flex items-center gap-3 mb-3 select-none no-underline group"
              aria-label="AV Simulation Studio — home"
            >
              <img
                src="/branding/avst-logo.png"
                alt=""
                className="h-14 w-auto select-none drop-shadow-[0_0_18px_rgba(56,189,248,0.35)] transition-transform group-hover:scale-105"
                draggable={false}
              />
              <span className="text-white font-bold tracking-tight text-xl">AV Simulation Studio</span>
            </Link>
            <p className="text-[11px] text-zinc-500 tracking-[0.28em] uppercase">
              Powered by Ideation Audio Visual
            </p>
          </div>

          {/* Glass card */}
          <div className="auth-glass rounded-2xl p-7">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white">Sign In</h2>
              <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-cyan-300/80">
                <ShieldCheck className="w-3 h-3" /> Secure
              </span>
            </div>

            {error && (
              <div
                className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg p-3 mb-4"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-zinc-300 text-xs tracking-widest uppercase">Email</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="auth-input pl-10 h-11 rounded-xl placeholder:text-zinc-600"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-zinc-300 text-xs tracking-widest uppercase">Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="auth-input pl-10 pr-10 h-11 rounded-xl placeholder:text-zinc-600"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-cyan-300 transition-colors"
                    data-testid="login-toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="auth-submit w-full h-11 rounded-xl text-white font-semibold disabled:opacity-70"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in…
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="w-4 h-4" /> Sign In
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <Link
                to="/forgot-password"
                className="text-xs text-zinc-400 hover:text-cyan-300 transition-colors tracking-wider"
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </Link>
            </div>

            <div className="mt-5 pt-5 border-t border-zinc-800/70 text-center">
              <p className="text-[11px] text-zinc-500 mb-2 tracking-wide uppercase">
                New here? Get 15 days free demo
              </p>
              <Link
                to="/signup"
                state={{ from: redirectTo }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 text-cyan-200 hover:border-cyan-300/60 hover:text-white transition-colors text-sm font-medium"
                data-testid="login-signup-link"
              >
                Create an account →
              </Link>
              <div className="mt-3">
                <Link
                  to="/purchase"
                  className="inline-flex items-center gap-1.5 text-xs text-fuchsia-300 hover:text-fuchsia-200 transition-colors tracking-wider"
                  data-testid="login-purchase-link"
                >
                  Demo expired? <span className="underline underline-offset-2">Purchase a plan →</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Footer: contact + copyright */}
          <div className="mt-6 text-center space-y-3">
            <div className="flex items-center justify-center gap-4 text-[11px] text-zinc-500">
              <a
                href="https://www.ideationav.com"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 hover:text-cyan-300 transition-colors"
              >
                <Globe className="w-3 h-3" /> www.ideationav.com
              </a>
              <span className="text-zinc-700">•</span>
              <a
                href="mailto:admin@ideationav.com"
                className="inline-flex items-center gap-1.5 hover:text-cyan-300 transition-colors"
              >
                <MailIcon className="w-3 h-3" /> admin@ideationav.com
              </a>
              <span className="text-zinc-700">•</span>
              <a
                href="https://www.instagram.com/avsimulationtool/"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram @avsimulationtool"
                className="inline-flex items-center gap-1.5 hover:text-fuchsia-300 transition-colors"
                data-testid="login-footer-instagram"
              >
                <Instagram className="w-3 h-3" /> @avsimulationtool
              </a>
            </div>
            <p className="text-[10px] text-zinc-600 leading-relaxed">
              © 2026 Ideation Audio Visual. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
